import classnames from 'classnames'

import { SIGNING_METHODS, DEFAULT_CLASSES } from '../constants'

import type { SigningMethod } from '@features/cpn.hooks/useWalletValidator'
import type { ValidationResult } from '@features/cpn.script/lib'

// Component-specific types
interface ValidationResultsProps {
  validationResult: ValidationResult
  selectedMethod: SigningMethod | null
  onRestart: () => void
  onRetry: () => void
}

/**
 * Get CSS classes for validation result title.
 */
const getValidationTitleClass = (success: boolean) => {
  return `type-body-base-bold ${success ? 'text-green-900' : 'text-red-900'}`
}

/**
 * Validation results component for the final step of wallet compatibility testing
 * Displays whether the signature validation was successful or failed.
 */
export const ValidationResults: React.FC<ValidationResultsProps> = ({
  validationResult,
  selectedMethod,
  onRestart,
  onRetry,
}) => {
  // Get method configuration for display
  const methodConfig = selectedMethod
    ? SIGNING_METHODS.find((m) => m.value === selectedMethod)
    : null

  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-2 text-neutral-strong type-h-title-md">
          Validation Results
        </h4>
      </div>

      {/* Validation Result Display */}
      <div
        className={classnames(
          'rounded-md border p-4',
          validationResult.success
            ? 'border-green-200 bg-green-50'
            : 'border-red-200 bg-red-50',
        )}
      >
        <div className="flex items-start">
          <div
            className={classnames(
              'shrink-0 text-2xl',
              validationResult.success ? 'text-green-600' : 'text-red-600',
            )}
          >
            {validationResult.success ? '✅' : '❌'}
          </div>
          <div className="ml-3">
            <h5 className={getValidationTitleClass(validationResult.success)}>
              {validationResult.success
                ? 'Validation Successful!'
                : 'Validation Failed'}
            </h5>
            <p
              className={classnames(
                'mt-1 text-sm',
                validationResult.success ? 'text-green-800' : 'text-red-800',
              )}
            >
              {validationResult.message}
            </p>
            {validationResult.details && (
              <p
                className={classnames(
                  'mt-2 font-mono text-xs',
                  validationResult.success ? 'text-green-700' : 'text-red-700',
                )}
              >
                {validationResult.details}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {validationResult.success && methodConfig && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
          <div className="text-sm text-blue-800">
            <strong>🎉 Great!</strong> Your wallet successfully signed the{' '}
            {methodConfig.label} payload. This means your wallet is compatible
            with Circle Payments Network for this signing method.
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          className={DEFAULT_CLASSES.button.primary}
          data-testid="restart-button"
          onClick={onRestart}
          type="button"
        >
          Test Another Method
        </button>
        <button
          className={DEFAULT_CLASSES.button.secondary}
          data-testid="retry-button"
          onClick={onRetry}
          type="button"
        >
          Try Different Signature
        </button>
      </div>
    </div>
  )
}
